import React, { useEffect, useState } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import { SlDrawer, SlProgressBar } from "@shoelace-style/shoelace/dist/react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation } from "swiper";
import API from "../../services/api";
import { langoptions } from "../../utils/constants";
import { Toast } from "../../shared/Toast";

const defaultEntry = {
  company: "",
  brand_id: "",
  sales: "",
};

const defaultGalleryField = {
  img_id: "",
  image: "",
  click_url: "",
  impression_url: "",
  impression_url_secondary: "",
};

const today = new Date();
today.setHours(0, 0, 0, 0);

const schema = yup.object({
  order_name: yup.string().required("Order name is required"),
  internal: yup.boolean(),
  gst: yup.boolean(),
  position: yup
    .number()
    .typeError("Position is required")
    .positive("Position must be positive")
    .required("Position is required"),
  shuffle: yup.boolean(),
  magazine_ads: yup.boolean(),
  ispdf: yup.string().when("magazine_ads", {
    is: true,
    then: (s) => s.required("Share type is required"),
    otherwise: (s) => s.optional(),
  }),
  dateRange: yup
    .object({
      startDate: yup
        .date()
        .typeError("Start date is required")
        .min(today, "Start date cannot be in the past")
        .required("Start date is required"),
      endDate: yup
        .date()
        .typeError("End date is required")
        .required("End date is required")
        .min(yup.ref("startDate"), "End date cannot be before start date"),
    })
    .required("Date range is required"),
  lang_ids: yup
    .array(yup.number())
    .min(1, "Language is required")
    .required("Language is required"),
  state_ids: yup.array(yup.number()).optional(),
  district_ids: yup.array(yup.number()).optional(),
  constituency_ids: yup.array(yup.number()).optional(),
  price: yup
    .number()
    .typeError("Price is required")
    .positive("Price must be positive")
    .required("Price is required"),
  // Repeatable company/brand/sales entries
  entries: yup.array().of(
    yup.object({
      company: yup.string().required("Company is required"),
      brand_id: yup
        .number()
        .typeError("Brand is required")
        .positive("Brand is required")
        .required("Brand is required"),
      sales: yup.string().required("Sales person is required"),
    })
  ),
});

function MultiOrder({ open, loadOrders, companies, sales }) {
  const [salesOptions] = useState([{ label: "Choose Sales person", value: "" }, ...sales]);
  const [companyoptions] = useState([{ label: "Select Company", value: "" }, ...companies]);

  // Top-form location state
  const [stateOptions, setStateOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [constituencyOptions, setConstituencyOptions] = useState([]);

  // Per-entry brand options (indexed by entry row)
  const [entryBrandOptions, setEntryBrandOptions] = useState([[]])

  // Gallery creative state
  const [galleryFields, setGalleryFields] = useState([{ ...defaultGalleryField }]);
  const [galleryProgress, setGalleryProgress] = useState(0);
  const [galleryProcessingIndex, setGalleryProcessingIndex] = useState(-1);

  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      order_name: "",
      internal: false,
      gst: false,
      position: 1,
      shuffle: false,
      magazine_ads: true,
      ispdf: "1",
      dateRange: { startDate: null, endDate: null },
      lang_ids: [],
      state_ids: [],
      district_ids: [],
      constituency_ids: [],
      price: "",
      entries: [defaultEntry],
    },
    mode: "onBlur",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "entries",
  });

  const watchMagazine = watch("magazine_ads");
  const watchPdf = watch("ispdf");

  useEffect(() => {
    setEntryBrandOptions((prev) => {
      const copy = [...prev];
      while (copy.length < fields.length) copy.push([]);
      return copy.slice(0, fields.length);
    });
  }, [fields.length]);

  // ── Top-form handlers ──

  const filterStatesByLangs = (stateIds, options, langs) =>
    (stateIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && langs.includes(matched.lang_id);
    });

  const filterDistrictIdsByStates = (districtIds, options, states) =>
    (districtIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && states.includes(matched.state_id);
    });

  const filterConstituencyIdsByDistricts = (constituencyIds, options, districts) =>
    (constituencyIds || []).filter((id) => {
      const matched = (options || []).find((opt) => opt.value === id);
      return matched && districts.includes(matched.dist_id);
    });

  const handleLangChange = async (selected) => {
    const langs = selected?.map((item) => item.value) || [];
    const currentStates = getValues("state_ids") || [];
    const currentDistricts = getValues("district_ids") || [];
    const currentConstituencies = getValues("constituency_ids") || [];

    const filteredStates = filterStatesByLangs(currentStates, stateOptions, langs);
    const filteredDistricts = filterDistrictIdsByStates(currentDistricts, districtOptions, filteredStates);
    const filteredConstituencies = filterConstituencyIdsByDistricts(currentConstituencies, constituencyOptions, filteredDistricts);

    setValue("lang_ids", langs, { shouldValidate: true });
    setValue("state_ids", filteredStates, { shouldValidate: true });
    setValue("district_ids", filteredDistricts, { shouldValidate: true });
    setValue("constituency_ids", filteredConstituencies, { shouldValidate: true });

    if (!langs.length) return;
    try {
      const res = await API({
        url: "/location/getStatesByLangId",
        method: "POST",
        responseType: "json",
        data: langs,
      });
      const options =
        res?.data?.map((item) => ({
          value: item.state_id,
          label: item.state_name,
          lang_id: item.lang_id,
        })) || [];
      setStateOptions(options);
    } catch (error) {
      console.error("Failed to load states", error);
    }
  };

  const handleStateChange = async (selected) => {
    const states = selected?.map((item) => item.value) || [];
    const langs = getValues("lang_ids") || [];
    const currentDistricts = getValues("district_ids") || [];
    const filteredDistrictOptions = (districtOptions || []).filter((opt) =>
      states.includes(opt.state_id)
    );
    const filteredDistricts = filterDistrictIdsByStates(currentDistricts, filteredDistrictOptions, states);
    const currentConstituencies = getValues("constituency_ids") || [];
    const filteredConstituencyOptions = (constituencyOptions || []).filter((opt) =>
      filteredDistricts.includes(opt.dist_id)
    );
    const filteredConstituencies = filterConstituencyIdsByDistricts(currentConstituencies, filteredConstituencyOptions, filteredDistricts);

    setValue("state_ids", states, { shouldValidate: true });
    setValue("district_ids", filteredDistricts, { shouldValidate: true });
    setValue("constituency_ids", filteredConstituencies, { shouldValidate: true });
    setDistrictOptions(filteredDistrictOptions);
    setConstituencyOptions(filteredConstituencyOptions);

    if (!states.length) return;
    try {
      const res = await API({
        url: "/location/getDistrictsByStateId",
        method: "POST",
        responseType: "json",
        data: { states, langs },
      });
      const options =
        res?.data?.map((item) => ({
          value: item.dist_id,
          label: item.dist_name,
          state_id: item.state_id,
          lang_id: item.lang_id,
        })) || [];
      setDistrictOptions(options);
    } catch (error) {
      console.error("Failed to load districts", error);
    }
  };

  const handleDistrictChange = async (selected) => {
    const districts = selected?.map((item) => item.value) || [];
    const langs = getValues("lang_ids") || [];
    const currentConstituencies = getValues("constituency_ids") || [];
    const filteredConstituencies = filterConstituencyIdsByDistricts(
      currentConstituencies,
      constituencyOptions,
      districts
    );

    setValue("district_ids", districts, { shouldValidate: true });
    setValue("constituency_ids", filteredConstituencies, { shouldValidate: true });

    if (!districts.length) return;
    try {
      const res = await API({
        url: "/location/getConstituentsnew",
        method: "POST",
        responseType: "json",
        data: { districts, langs },
      });
      const options =
        res?.data?.map((item) => ({
          value: item.const_id,
          label: item.const_name,
          dist_id: item.dist_id,
          lang_id: item.lang_id,
        })) || [];
      setConstituencyOptions(options);
    } catch (error) {
      console.error("Failed to load constituencies", error);
    }
  };

  // ── Per-entry handlers ──

  const handleEntrySalesChange = async (selected, index, fieldOnChange) => {
    const salesId = selected?.value || "";
    fieldOnChange(salesId);
    setValue(`entries.${index}.brand_id`, "");
    setEntryBrandOptions((prev) => {
      const next = [...prev];
      next[index] = [];
      return next;
    });
    if (!salesId) return;
    try {
      const res = await API({
        url: "/order/fetch-brands",
        method: "POST",
        responseType: "json",
        data: { sales_id: salesId, type: "all" },
      });
      if (res.code === 200) {
        setEntryBrandOptions((prev) => {
          const next = [...prev];
          next[index] = res.data || [];
          return next;
        });
      }
    } catch (error) {
      console.error("Failed to load brands", error);
    }
  };

  const copyPreviousEntry = (index) => {
    if (index <= 0) return;
    const prevEntry = getValues(`entries.${index - 1}`);
    if (!prevEntry) return;
    Object.keys(defaultEntry).forEach((key) => {
      setValue(`entries.${index}.${key}`, prevEntry[key], {
        shouldValidate: true,
        shouldDirty: true,
      });
    });
    setEntryBrandOptions((prev) => {
      const next = [...prev];
      next[index] = prev[index - 1] || [];
      return next;
    });
  };

  // ── Gallery creative handlers ──

  const galleryImageUrl = async (imgFile, index) => {
    try {
      const formData = new FormData();
      formData.append("files", imgFile);
      const res = await API({
        url: "/upload/image",
        method: "POST",
        responseType: "json",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (data) => {
          setGalleryProgress(Math.round((100 * data.loaded) / data.total));
        },
      });
      if (res.code === 200) {
        setGalleryFields((prev) => {
          const next = [...prev];
          next[index] = { ...next[index], image: res.data.data, img_id: `${index}` };
          return next;
        });
      } else {
        setGalleryProgress(0);
        Toast("error", res.data.message);
      }
    } catch (error) {
      setGalleryProgress(0);
      Toast("error", "Image upload failed");
    }
  };

  const galleryImageChange = (e, index) => {
    if (e.target.files && e.target.files.length > 0) {
      setGalleryProcessingIndex(index);
      galleryImageUrl(e.target.files[0], index);
    } else {
      setGalleryProgress(0);
      setGalleryFields((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], image: "" };
        return next;
      });
    }
  };

  const addGalleryImage = () => {
    if ((watchMagazine && galleryFields.length < 30) || (!watchMagazine && galleryFields.length < 10)) {
      setGalleryFields((prev) => [...prev, { ...defaultGalleryField }]);
    } else {
      Toast(
        "error",
        watchMagazine
          ? "For magazine only limit of 30 images accepted"
          : "Maximum limit of 10 images exceeded"
      );
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryFields((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next;
    });
  };

  const handleGalleryClickUrl = (index, e) => {
    setGalleryFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], click_url: e.target.value };
      return next;
    });
  };

  const handleGalleryImpUrl = (index, e) => {
    setGalleryFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], impression_url: e.target.value };
      return next;
    });
  };

  const handleGalleryImpUrl2 = (index, e) => {
    setGalleryFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], impression_url_secondary: e.target.value };
      return next;
    });
  };

  // ── Submit ──

  const onSubmit = async (data) => {
    // Validate gallery
    if (galleryFields.length < 2) {
      Toast("error", "Minimum 2 images required for creative");
      return;
    }
    if (galleryFields.some((f) => !f.image)) {
      Toast("error", "Please upload all gallery images");
      return;
    }

    try {
      const {
        order_name, internal, gst, position,
        shuffle, magazine_ads, ispdf,
        dateRange, lang_ids, state_ids, district_ids, constituency_ids,
        price, entries,
      } = data;

      const orders = entries.map((entry) => ({
        company: entry.company,
        brand_id: entry.brand_id,
        sales: entry.sales,
        order_name,
        internal,
        gst,
        start_date: dateRange?.startDate,
        end_date: dateRange?.endDate,
        dateRange,
        lang_ids,
        state_ids,
        district_ids,
        constituency_ids,
        quantity: 1,
        price,
      }));

      const creative_data = {
        inputFields: galleryFields,
        shuffle: shuffle || false,
        magazine_ads: magazine_ads || false,
        isPdf: magazine_ads ? (ispdf || "1") : undefined,
        creative_name: order_name,
      };

      const res = await API({
        url: "/order/create-multi-order",
        responseType: "json",
        method: "POST",
        data: { orders, position, creative_data },
      });
      if (res.code === 200) {
        Toast("success", "Order Created Successfully!");
        loadOrders();
      }
    } catch (error) {
      console.error(error, "Error Submitting Multi Order");
    }
  };

  function handleRequestClose(event) {
    if (event.detail.source === "overlay") {
      event.preventDefault();
      return;
    }
    loadOrders();
  }

  return (
    <SlDrawer open={open} onSlRequestClose={handleRequestClose} style={{ "--size": window.innerWidth <= 767 ? "100vw" : "50vw" }}>
      <div className="min-h-screen bg-gray-50 text-slate-800 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-500">Bulk Magazine create orders</p>
              <h1 className="text-2xl font-semibold">Multi Order Entry</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

            {/* ── TOP FORM: Order Details ── */}
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Order Details</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                {/* Order Name */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Order Name</label>
                  <input
                    type="text"
                    {...register("order_name")}
                    className="form-control"
                    placeholder="Quarterly push campaign"
                  />
                  {errors.order_name && (
                    <span className="text-xs text-red-600">{errors.order_name.message}</span>
                  )}
                </div>

                {/* Price */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    {...register("price")}
                    className="form-control"
                    placeholder="e.g. 4999"
                  />
                  {errors.price && (
                    <span className="text-xs text-red-600">{errors.price.message}</span>
                  )}
                </div>

                {/* Position */}
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Position</label>
                  <input
                    type="number"
                    min="1"
                    {...register("position")}
                    className="form-control"
                    placeholder="e.g. 1"
                  />
                  {errors.position && (
                    <span className="text-xs text-red-600">{errors.position.message}</span>
                  )}
                </div>

                {/* Internal & GST */}
                <div className="flex items-center gap-6 pt-5">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register("internal")}
                      className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                      id="internal-top"
                    />
                    <label htmlFor="internal-top" className="text-sm text-slate-700">Internal</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register("gst")}
                      className="h-4 w-4 rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                      id="gst-top"
                    />
                    <label htmlFor="gst-top" className="text-sm text-slate-700">GST</label>
                  </div>
                </div>

              </div>

              {/* Languages */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Languages</label>
                  <Controller
                    control={control}
                    name="lang_ids"
                    render={({ field }) => (
                      <Select
                        isMulti
                        className="text-sm"
                        classNamePrefix="react-select"
                        options={langoptions}
                        value={langoptions.filter((opt) => (field.value || []).includes(opt.value))}
                        onChange={(selected) => handleLangChange(selected)}
                        closeMenuOnSelect={false}
                        placeholder="Select languages"
                      />
                    )}
                  />
                  {errors.lang_ids && (
                    <span className="text-xs text-red-600">{errors.lang_ids.message}</span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">States</label>
                  <Controller
                    control={control}
                    name="state_ids"
                    render={({ field }) => (
                      <Select
                        isMulti
                        className="text-sm"
                        classNamePrefix="react-select"
                        options={stateOptions}
                        value={stateOptions.filter((opt) => (field.value || []).includes(opt.value))}
                        onChange={(selected) => handleStateChange(selected)}
                        closeMenuOnSelect={false}
                        placeholder="Select states"
                      />
                    )}
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Districts</label>
                  <Controller
                    control={control}
                    name="district_ids"
                    render={({ field }) => (
                      <Select
                        isMulti
                        className="text-sm"
                        classNamePrefix="react-select"
                        options={districtOptions}
                        value={districtOptions.filter((opt) => (field.value || []).includes(opt.value))}
                        onChange={(selected) => handleDistrictChange(selected)}
                        closeMenuOnSelect={false}
                        placeholder="Select districts"
                      />
                    )}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Constituencies</label>
                  <Controller
                    control={control}
                    name="constituency_ids"
                    render={({ field }) => (
                      <Select
                        isMulti
                        className="text-sm"
                        classNamePrefix="react-select"
                        options={constituencyOptions}
                        value={constituencyOptions.filter((opt) => (field.value || []).includes(opt.value))}
                        onChange={(selected) =>
                          setValue("constituency_ids", selected?.map((item) => item.value) || [], {
                            shouldValidate: true,
                          })
                        }
                        closeMenuOnSelect={false}
                        placeholder="Select constituencies"
                      />
                    )}
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="mt-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-slate-700">Date Range</label>
                  <Controller
                    control={control}
                    name="dateRange"
                    render={({ field }) => (
                      <div className="w-full">
                        <Datepicker
                          value={field.value || { startDate: null, endDate: null }}
                          onChange={(val) => field.onChange(val)}
                          useRange
                          asSingle={false}
                          primaryColor="slate"
                          popoverDirection="down"
                          readOnly={false}
                          showShortcuts={false}
                          minDate={today}
                          toggleClassName="hidden"
                          containerClassName="w-full relative"
                          inputClassName="form-control"
                          displayFormat="DD/MM/YYYY"
                        />
                      </div>
                    )}
                  />
                  {(errors?.dateRange?.startDate || errors?.dateRange?.endDate || errors?.dateRange?.message) && (
                    <span className="text-xs text-red-600">
                      {errors?.dateRange?.startDate?.message ||
                        errors?.dateRange?.endDate?.message ||
                        errors?.dateRange?.message}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* ── GALLERY CREATIVE ── */}
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-700">Creative (Gallery)</h2>
                <button
                  type="button"
                  className="btn btn-outline-primary text-xs"
                  onClick={addGalleryImage}
                >
                  + Add Image
                </button>
              </div>

              <div className="space-y-4">
                {galleryFields.map((gField, index) => (
                  <div
                    key={index}
                    className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs font-medium text-slate-600">Image {index + 1}</label>
                      {galleryFields.length > 1 && (
                        <button
                          type="button"
                          className="text-xs text-red-600 hover:text-red-700"
                          onClick={() => removeGalleryImage(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <span className="text-xs text-slate-400">(Image height should be greater than width)</span>

                    <input
                      className="form-control mb-2 mt-1"
                      type="file"
                      accept="image/*"
                      onChange={(e) => galleryImageChange(e, index)}
                    />

                    {galleryProcessingIndex === index && galleryProgress > 0 && galleryProgress < 100 ? (
                      <SlProgressBar value={galleryProgress}>{galleryProgress}%</SlProgressBar>
                    ) : gField.image ? (
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-success text-xs">✓</span>
                        <img src={gField.image} alt={`preview-${index}`} className="h-12 w-auto rounded border" />
                        <span className="text-xs text-slate-500 truncate max-w-xs">{gField.image}</span>
                      </div>
                    ) : null}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                      <div>
                        <label className="text-xs text-slate-500">Click URL / Mobile No.</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue={gField.click_url}
                          onChange={(e) => handleGalleryClickUrl(index, e)}
                          placeholder="https://... or 9XXXXXXXXX"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Impression URL - Primary</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue={gField.impression_url}
                          onChange={(e) => handleGalleryImpUrl(index, e)}
                          placeholder="https://..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-slate-500">Impression URL - Secondary</label>
                        <input
                          type="text"
                          className="form-control"
                          defaultValue={gField.impression_url_secondary}
                          onChange={(e) => handleGalleryImpUrl2(index, e)}
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Gallery options */}
              <div className="mt-3 flex flex-wrap gap-4 items-start">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="shuffle"
                    className="h-4 w-4 rounded border-slate-300"
                    {...register("shuffle")}
                  />
                  <label htmlFor="shuffle" className="text-sm text-slate-700">Shuffle</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="magazine_ads"
                    className="h-4 w-4 rounded border-slate-300"
                    {...register("magazine_ads")}
                  />
                  <label htmlFor="magazine_ads" className="text-sm text-slate-700">Magazine Ads</label>
                </div>
              </div>

              {watchMagazine && (
                <div className="mt-3">
                  <label className="text-sm text-slate-700 mb-1 block">Share Type</label>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="ispdf-image"
                        value="1"
                        {...register("ispdf")}
                        checked={watchPdf === "1"}
                      />
                      <label htmlFor="ispdf-image" className="text-sm text-slate-700">Image</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        id="ispdf-pdf"
                        value="2"
                        {...register("ispdf")}
                        checked={watchPdf === "2"}
                      />
                      <label htmlFor="ispdf-pdf" className="text-sm text-slate-700">PDF</label>
                    </div>
                  </div>
                  {errors.ispdf && (
                    <span className="text-xs text-red-600">{errors.ispdf.message}</span>
                  )}
                </div>
              )}

              {/* Inline preview of uploaded images */}
              {galleryFields.some((f) => f.image) && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-slate-600 mb-2">Preview</p>
                  <div className="preview-inner">
                    <div className="preview-gallery">
                      <Swiper
                        spaceBetween={50}
                        pagination={true}
                        navigation={true}
                        modules={[Navigation, Pagination]}
                        className="mySwiper h-full"
                      >
                        {galleryFields.map((f, i) =>
                          f.image ? (
                            <SwiperSlide key={i}>
                              <img src={f.image} alt={`gallery-${i}`} className="w-full !h-[95%]" />
                            </SwiperSlide>
                          ) : null
                        )}
                        <img src="https://d30365bzukh5e5.cloudfront.net/share_content_imgs/telugu_new3_v8.png" alt="way2news-logo" />
                      </Swiper>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── PER-ENTRY ROWS: Company / Brand / Sales ── */}
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm px-4 py-4">
              <h2 className="text-sm font-semibold text-slate-700 mb-3">Company / Brand / Sales</h2>
              <div className="space-y-3">
                {fields.map((field, index) => {
                  const entryError = errors?.entries?.[index] || {};
                  const brands = entryBrandOptions[index] || [];

                  return (
                    <div
                      key={field.id}
                      className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start rounded-lg border border-slate-100 bg-slate-50 px-3 py-3"
                    >
                      {/* Company */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-slate-500">Company</label>
                        <Controller
                          control={control}
                          name={`entries.${index}.company`}
                          render={({ field: f }) => (
                            <Select
                              className="text-sm"
                              classNamePrefix="react-select"
                              options={companyoptions}
                              value={companyoptions.find((o) => o.value === f.value) || companyoptions[0]}
                              onChange={(selected) => f.onChange(selected?.value || "")}
                              isSearchable
                            />
                          )}
                        />
                        {entryError.company && (
                          <span className="text-xs text-red-600">{entryError.company.message}</span>
                        )}
                      </div>

                      {/* Sales */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-slate-500">Sales</label>
                        <Controller
                          control={control}
                          name={`entries.${index}.sales`}
                          render={({ field: f }) => (
                            <Select
                              className="text-sm"
                              classNamePrefix="react-select"
                              options={salesOptions}
                              value={salesOptions.find((o) => o.value === f.value) || salesOptions[0]}
                              onChange={(selected) => handleEntrySalesChange(selected, index, f.onChange)}
                              isSearchable
                            />
                          )}
                        />
                        {entryError.sales && (
                          <span className="text-xs text-red-600">{entryError.sales.message}</span>
                        )}
                      </div>

                      {/* Brand */}
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-slate-500">Brand</label>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <Controller
                              control={control}
                              name={`entries.${index}.brand_id`}
                              render={({ field: f }) => (
                                <Select
                                  className="text-sm"
                                  classNamePrefix="react-select"
                                  options={brands}
                                  value={brands.find((o) => o.value === f.value) || null}
                                  onChange={(selected) => f.onChange(selected?.value || "")}
                                  placeholder={brands.length ? "Select a brand" : "Select Sales first"}
                                  isDisabled={!brands.length}
                                  isSearchable
                                />
                              )}
                            />
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {index > 0 && (
                              <button
                                type="button"
                                onClick={() => copyPreviousEntry(index)}
                                className="text-xs text-blue-600 hover:text-blue-700 whitespace-nowrap"
                              >
                                Copy above
                              </button>
                            )}
                            {fields.length > 1 && (
                              <button
                                type="button"
                                onClick={() => remove(index)}
                                className="text-xs text-red-600 hover:text-red-700"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                        {entryError.brand_id && (
                          <span className="text-xs text-red-600">{entryError.brand_id.message}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <button
                type="button"
                onClick={() => append(defaultEntry)}
                className="btn btn-outline-primary d-inline-flex align-items-center gap-2 text-sm"
              >
                <span className="text-lg leading-none">+</span>
                Add another company
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-primary text-sm"
              >
                {isSubmitting ? "Saving..." : "Save all orders"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </SlDrawer>
  );
}

export default MultiOrder;
